import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const inputCls = "bg-[#121212] border-axe-border rounded-xl text-white h-12 px-4";
const taCls = "bg-[#121212] border-axe-border rounded-xl text-white p-4";

export default function FaqEditor() {
  const [faqs, setFaqs] = useState([]);
  const [form, setForm] = useState(null); // {question, answer, order, _editId}
  const [saving, setSaving] = useState(false);
  const [confirm, ConfirmModal] = useConfirm();

  async function refresh() {
    const { data } = await api.get("/faqs");
    setFaqs(data);
  }
  useEffect(() => {
    refresh();
  }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { question: form.question, answer: form.answer, order: Number(form.order) || 0 };
      if (form._editId) await api.put(`/faqs/${form._editId}`, payload);
      else await api.post("/faqs", payload);
      toast.success("Saved");
      setForm(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    if (!(await confirm("Delete this FAQ?"))) return;
    try {
      await api.delete(`/faqs/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div data-testid="faq-editor">
      <ConfirmModal />
      <div className="flex justify-end mb-5">
        <button onClick={() => setForm({ question: "", answer: "", order: faqs.length })} className="inline-flex items-center gap-2 rounded-full bg-axe-tangerine px-5 py-2 text-xs uppercase tracking-[0.22em] font-bold text-black hover:bg-axe-tangerine-hover" data-testid="faq-new">
          <Plus size={14} /> New FAQ
        </button>
      </div>
      <div className="space-y-3">
        {faqs.map((f) => (
          <div key={f.id} className="rounded-2xl border border-axe-border bg-axe-surface p-5" data-testid={`faq-row-${f.id}`}>
            <div className="flex justify-between gap-4">
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-axe-purple font-bold">Order {f.order}</div>
                <div className="mt-1 font-display font-semibold text-base text-white">{f.question}</div>
                <div className="mt-2 text-sm text-neutral-400 line-clamp-2">{f.answer}</div>
              </div>
              <div className="flex gap-3 self-start">
                <button onClick={() => setForm({ question: f.question, answer: f.answer, order: f.order, _editId: f.id })} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-axe-tangerine hover:underline" data-testid={`faq-edit-${f.id}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => remove(f.id)} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] font-bold text-red-400 hover:underline" data-testid={`faq-delete-${f.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
        {faqs.length === 0 && <div className="text-neutral-500 border border-axe-border rounded-2xl p-8 text-center">No FAQs yet.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="faq-modal">
          <form onSubmit={save} className="relative w-full max-w-2xl bg-axe-surface border border-axe-border rounded-2xl p-6 md:p-8 space-y-5 my-10">
            <button type="button" onClick={() => setForm(null)} className="absolute right-4 top-4 p-2 text-neutral-400 hover:text-white" aria-label="Close"><X size={18} /></button>
            <h3 className="font-display font-black text-2xl">{form._editId ? "Edit FAQ" : "New FAQ"}</h3>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Question</Label>
              <Input required value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className={`mt-2 ${inputCls}`} data-testid="faq-q" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Answer</Label>
              <Textarea required value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className={`mt-2 ${taCls} min-h-[140px]`} data-testid="faq-a" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.25em] text-neutral-400">Order (sort key)</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={`mt-2 ${inputCls} max-w-[120px]`} data-testid="faq-order" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setForm(null)} className="rounded-full border border-axe-border px-6 py-2.5 text-xs uppercase tracking-[0.22em] font-bold hover:bg-axe-bg">Cancel</button>
              <button type="submit" disabled={saving} className="rounded-full bg-axe-purple px-7 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-axe-purple-hover disabled:opacity-50" data-testid="faq-save">
                {saving ? "Saving…" : form._editId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
